import {
  LogItem,
  RequestRefundTransactionLog,
  RequestRefundType,
  TransactionLogType,
} from '@back-office/http';
import { compareAsc, isValid, parseJSON } from 'date-fns';
import { applySnapshot, getSnapshot, types as t } from 'mobx-state-tree';

const SourceOfFund = t.model('SourceOfFund', {
  code: t.optional(t.string, ''),
  name: t.optional(t.string, ''),
});

const ChildModel = t.model('Child', {
  amount: t.optional(t.string, ''),
  id: t.optional(t.string, ''),
  ref1: t.optional(t.string, ''),
  ref2: t.maybeNull(t.string),
  updatedAt: t.optional(t.string, ''),
  updatedBy: t.optional(t.string, ''),
  type: t.maybeNull(t.string),
});

const RequestRefundModel = t.model('RequestRefund', {
  id: t.string,
  requestDescription: t.string,
  requestTypeReason: t.string,
  requestedAt: t.string,
  requestedEmail: t.string,
  status: t.string,
  rejectedAt: t.optional(t.string, ''),
  approvedAt: t.optional(t.string, ''),
  cancelledAt: t.optional(t.string, ''),
  approveDescription: t.maybeNull(t.string),
  approvedEmail: t.optional(t.string, ''),
  rejectDescription: t.optional(t.string, ''),
  rejectTypeReason: t.optional(t.string, ''),
  rejectedEmail: t.optional(t.string, ''),
  cancelledEmail: t.optional(t.string, ''),
});

const TransactionModel = t
  .model('Transaction', {
    id: t.optional(t.string, ''),
    transactionID: t.optional(t.string, ''),
    updatedAt: t.optional(t.string, ''),
    updatedBy: t.optional(t.string, ''),
    amount: t.optional(t.string, ''),
    currency: t.optional(t.string, ''),
    status: t.optional(t.string, ''),
    ref1: t.optional(t.string, ''),
    ref2: t.maybeNull(t.string),
    qrType: t.maybeNull(t.string),
    description: t.maybeNull(t.string),
    payerName: t.maybeNull(t.string),
    fundsUID: t.maybeNull(t.string),
    sourceOfFund: t.maybeNull(SourceOfFund),
    mid: t.optional(t.string, ''),
    merchantName: t.optional(t.string, ''),
    merchantBusinessType: t.optional(t.string, ''),
    tid: t.optional(t.string, ''),
    rFeeType: t.maybeNull(t.string),
    rFeeAmount: t.maybeNull(t.string),
    rAcquirerMDRType: t.maybeNull(t.string),
    rAcquirerMDRAmount: t.maybeNull(t.string),
    rIssuerMDRType: t.maybeNull(t.string),
    rIssuerMDRAmount: t.maybeNull(t.string),
    transferAmount: t.maybeNull(t.string),
    rVATAmount: t.optional(t.string, ''),
    rWHTAmount: t.optional(t.string, ''),
    calFeeAmount: t.maybeNull(t.string),
    calVATAmount: t.maybeNull(t.string),
    calWHTAmount: t.maybeNull(t.string),
    calAcquirerMDRAmount: t.maybeNull(t.string),
    calIssuerMDRAmount: t.maybeNull(t.string),
    createdAt: t.maybeNull(t.string),
    confirmingAt: t.maybeNull(t.string),
    pendingAt: t.maybeNull(t.string),
    dropOffAt: t.maybeNull(t.string),
    authorizedAt: t.maybeNull(t.string),
    authorizedVoidedAt: t.maybeNull(t.string),
    rejectedAt: t.maybeNull(t.string),
    capturedAt: t.maybeNull(t.string),
    unmatchedAt: t.maybeNull(t.string),
    clearedAt: t.maybeNull(t.string),
    clearedPendingRefundAt: t.maybeNull(t.string),
    clearedRefundedAt: t.maybeNull(t.string),
    cancelledAt: t.maybeNull(t.string),
    refundRequestPendingAt: t.maybeNull(t.string),
    child: t.maybeNull(ChildModel),
    requestRefund: t.maybeNull(t.array(RequestRefundModel)),
  })
  .actions((self) => {
    let initialState = {};
    return {
      afterCreate() {
        initialState = getSnapshot(self);
      },
      reset() {
        applySnapshot(self, initialState);
      },
    };
  })
  .views((self) => ({
    get auditTrail() {
      if (self) {
        const logItems = TransactionLogType.reduce<LogItem[]>(
          (logItem, transactionLogType) => {
            const logDate = self[transactionLogType];
            if (logDate) {
              logItem.push({
                label: transactionLogType
                  .replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)
                  .slice(0, -3),
                date: logDate,
              });
            }
            return logItem;
          },
          []
        );
        if (self.requestRefund) {
          self.requestRefund.forEach((request: RequestRefundType) => {
            const requestRefundLogItems: LogItem[] =
              RequestRefundTransactionLog.reduce<LogItem[]>(
                (requestRefundLogItem, logStatus) => {
                  const logDate = request[logStatus];
                  if (logDate) {
                    switch (logStatus) {
                      case 'approvedAt': {
                        requestRefundLogItem.push({
                          label: 'refund_approved',
                          date: request.approvedAt,
                          description: request?.approveDescription,
                          email: request.approvedEmail,
                        });
                        break;
                      }
                      case 'requestedAt': {
                        requestRefundLogItem.push({
                          label: 'refund_requested',
                          date: request.requestedAt,
                          typeReason: request.requestTypeReason,
                          description: request.requestDescription,
                          email: request.requestedEmail,
                        });
                        break;
                      }
                      case 'rejectedAt': {
                        requestRefundLogItem.push({
                          label: 'refund_rejected',
                          date: request.rejectedAt,
                          typeReason: request.rejectTypeReason,
                          description: request.rejectDescription,
                          email: request.rejectedEmail,
                        });
                        break;
                      }
                      case 'cancelledAt': {
                        requestRefundLogItem.push({
                          label: 'refund_cancelled',
                          date: request.cancelledAt,
                          email: request.cancelledEmail,
                        });
                        break;
                      }
                    }
                  }
                  return requestRefundLogItem;
                },
                []
              );
            logItems.push(...requestRefundLogItems);
          });
        }

        const sortedLogItems = [...logItems]
          .filter((data) => (data.date ? isValid(parseJSON(data.date)) : false))
          .sort((a, b) => {
            const date1 = parseJSON(a.date as string);
            const date2 = parseJSON(b.date as string);
            return compareAsc(date1, date2);
          });
        return insertClearedAfterCancelledRejected(sortedLogItems);
      }
      return [];
    },
  }));

export default TransactionModel;

const insertClearedAfterCancelledRejected = (
  logItems: LogItem[]
): LogItem[] => {
  const clearedInserted = [...logItems];
  const indexForPush: number[] = [];
  const cleared: LogItem | undefined = [...logItems].find(
    (log) => log.label === 'cleared'
  );
  // For get index before insert cleared into array
  clearedInserted.forEach((log, index) => {
    if (log.label.includes('cancelled') || log.label.includes('rejected')) {
      indexForPush.push(index);
    }
  });
  if (cleared) {
    indexForPush.forEach((i, index) => {
      // Insert cleared at index is added with its index.
      clearedInserted.splice(i + index + 1, 0, cleared);
    });
  }
  return clearedInserted.reverse();
};
